export type ClubMode = "player" | "manager";

export type ClubOption = { label: string; value: number };

// Rows returned by get_clubs/1/ALL: [club_id, club_name, club_city, is_beta]
type AllClubsRow = [number, string, string, boolean?];

// Rows returned by get_clubs/<user_id>/<MANAGER|PLAYER>: [club_id, club_name, _, is_waitlisted]
type UserClubsRow = [number, string, unknown, boolean | number | null];

export function fetchUserId(
  devPort: string,
  userName: string,
  mode: ClubMode
): Promise<number | null> {
  const endpoint = mode === "manager" ? "system_get_manager/" : "system_get_player/";
  return fetch(devPort + endpoint + userName, { credentials: "include" })
    .then((response) => response.json())
    .then((data: [number | null][] | null) =>
      data && data[0] && data[0][0] !== null && data[0][0] !== undefined
        ? data[0][0]
        : null
    );
}

export function fetchUserEmail(devPort: string, userName: string): Promise<string> {
  return fetch(devPort + "system_get_user_email/" + userName, {
    credentials: "include",
  })
    .then((response) => response.json())
    .then((data: [string | null][] | null) => (data && data[0] && data[0][0]) || "")
    .catch(() => "");
}

export type PlayerName = { firstName: string; lastName: string };

/** Resolves to null when the user has no player profile or no name on record. */
export function fetchPlayerName(devPort: string, userName: string): Promise<PlayerName | null> {
  return fetch(devPort + "system_get_player_name/" + userName, { credentials: "include" })
    .then((response) => response.json())
    .then((data: [string | null, string | null][] | null) => {
      const firstName = (data && data[0] && data[0][0]) || "";
      const lastName = (data && data[0] && data[0][1]) || "";
      return firstName.trim() && lastName.trim() ? { firstName, lastName } : null;
    })
    .catch(() => null);
}

export function saveUserEmail(devPort: string, userName: string, email: string) {
  return fetch(devPort + "system_update_user_email", {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ user_name: userName, email: email }),
  }).then((response) => response.json());
}

export function fetchAllClubs(devPort: string, betaMode: boolean): Promise<ClubOption[]> {
  return fetch(devPort + "get_clubs/1/ALL", { credentials: "include" })
    .then((response) => response.json())
    .then((data: AllClubsRow[] | null) =>
      (data || [])
        .filter((info) => betaMode || !info[3])
        .map((info) => ({ label: info[1] + ":" + info[2], value: info[0] }))
    );
}

export function fetchUserClubs(
  devPort: string,
  userId: number,
  mode: ClubMode
): Promise<{ clubs: ClubOption[]; waitlistClubs: ClubOption[] }> {
  return fetch(
    devPort + "get_clubs/" + userId + "/" + (mode === "manager" ? "MANAGER" : "PLAYER"),
    { credentials: "include" }
  )
    .then((response) => response.json())
    .then((data: UserClubsRow[] | null) => {
      const clubs: ClubOption[] = [];
      const waitlistClubs: ClubOption[] = [];
      (data || []).forEach((info) => {
        const club = { label: info[1], value: info[0] };
        if (info[3]) {
          waitlistClubs.push(club);
        } else {
          clubs.push(club);
        }
      });
      return { clubs, waitlistClubs };
    });
}

/** Resolves to true when the club approves new players, i.e. the player joined its waitlist. */
export function joinClub(
  devPort: string,
  playerId: number,
  clubId: number,
  firstName: string,
  lastName: string
): Promise<boolean> {
  return fetch(devPort + "club_access/" + clubId, { credentials: "include" })
    .then((response) => response.json())
    .then((data: [boolean | number | null][] | null) => {
      const waitlist = !!(data && data[0] && data[0][0]);
      return fetch(devPort + "edit_player", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          player_id: playerId,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          uspa_rating: -2,
          club_rating: -2,
          club_id: clubId,
          is_club_pro: false,
          waitlist: waitlist,
        }),
      })
        .then((response) => response.json())
        .then(() => waitlist);
    });
}

export function fetchClubLogos(
  devPort: string,
  clubIds: number[]
): Promise<Record<number, string>> {
  return fetch(devPort + "club_logos/" + clubIds.join(","), { credentials: "include" })
    .then((response) => response.json())
    .then((data: [number, string][] | null) => {
      const logos: Record<number, string> = {};
      (data || []).forEach(([clubId, logo]) => {
        logos[clubId] = logo;
      });
      return logos;
    });
}

/** Pass null to remove the logo. */
export function saveClubLogo(
  devPort: string,
  clubId: number,
  logo: string | null
): Promise<boolean> {
  return fetch(devPort + "update_club_logo", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ club_id: clubId, club_logo: logo }),
  })
    .then((response) => response.json())
    .then((data: { message?: string } | null) => data?.message === "Successful Logo Change");
}

export type NewClub = {
  name: string;
  country: string;
  state: string;
  city: string;
};

export function registerClub(devPort: string, managerId: number, club: NewClub) {
  const path = [String(managerId), club.name.trim(), club.country, club.state.trim(), club.city.trim()]
    .map((part) => encodeURIComponent(part))
    .join("/");
  return fetch(devPort + "add_club/" + path, { credentials: "include" }).then((response) =>
    response.json()
  );
}
